import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Network, Plus, ShieldAlert, Upload, Building2 } from "lucide-react";
import { RegisterToolbar, SummaryTiles } from "../components/RegisterToolbar";
import { by, downloadCsv, matchesSearch, type SortOption } from "../lib/registerExport";
import { ZoneFlowMatrix } from "../components/ZoneFlowMatrix";
import { EstateUnassignedQueue, type EstateAsset } from "../components/EstateUnassignedQueue";
import { EstateAssetTable } from "../components/EstateAssetTable";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import { saveBlob } from "../lib/download";
import { LoadingBlock } from "../components/Spinner";
import { IndexTabs } from "../components/Drawing";
import {
  EmptyState, Field, ModalOverlay, ModalHeader, Check, input, textarea, secondaryBtn, dangerBtn, fmtDay, fmtDateOnly, toDateInput, todayInput, fromInput,
} from "./resilience/shared";
import { useCurrentUser } from "../auth/useCurrentUser";
import { KIND_HREFS } from "../components/EntityTags";
import { RelatedRecords, type RelatedLink } from "../components/RelatedRecords";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";

// Network zones, the OT inventory import and per-site physical security.
//
// One page rather than three because they answer one question three ways: what is out there, where
// does it sit, and who can walk up to it. Segmentation findings are computed per request, never
// stored — a stored copy would be wrong the moment anyone added a dependency.

type Tab = "zones" | "import" | "physical";

const SEVERITY: Record<string, { color: string; bg: string }> = {
  // Risk-band tokens, so the three schemes (dark / light / colour-blind) all apply.
  high:   { color: theme.color.riskHighText, bg: theme.color.riskHighBg },
  medium: { color: theme.color.riskMedText,  bg: theme.color.riskMedBg },
  low:    { color: theme.color.riskLowText,  bg: theme.color.riskLowBg },
};

interface Zone {
  id: string; name: string; purpose: string; trustLevel: number | null; description: string | null;
  site: { id: string; name: string } | null; _count: { assets: number };
}
interface Finding { id: string; kind: string; severity: string; title: string; detail: string; assetIds: string[]; zoneIds?: string[] }
interface Segmentation { total: number; high: number; medium: number; low: number; safetyExposed: number; undeclared?: number; denied?: number }
interface ImportColumn { key: string; label: string; required: boolean; help: string }
interface Catalogue {
  zonePurposes: string[]; importColumns: ImportColumn[]; physical: Record<string, string[]>;
  /** ZONE_PURPOSE_LABELS from backend lib/networkZones.ts ("dmz" → "DMZ / perimeter"). */
  zonePurposeLabels?: Record<string, string>;
  purdueLevels?: Array<{ value: number; label: string }>;
}
interface RowIssue { line: number; column: string | null; message: string }
interface ImportPlan {
  create: Array<{ line: number; name: string; reference: string }>;
  update: Array<{ line: number; name: string; reference: string }>;
  unchanged: number;
  retire: Array<{ name: string; reference: string }>;
  issues: RowIssue[]; unknownColumns: string[]; unknownZones: string[]; unknownSites: string[];
}
interface PhysicalRecord {
  siteId: string; siteName: string; siteKind: string; inScope: boolean;
  accessControl: string | null; visitorManagement: string | null; cctvCoverage: string | null;
  cctvRetentionDays: number | null; intruderAlarm: boolean | null; alarmMonitored: boolean | null;
  environmentalMonitoring: string[]; fireSuppression: string | null; powerResilience: string[];
  powerTestedAt: string | null; secureAreas: boolean | null; clearDeskPolicy: boolean | null;
  equipmentDisposal: string | null; reviewedAt: string | null;
}
interface PhysicalFinding { id: string; siteId: string; severity: string; title: string; detail: string; control: string }

const words = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
/** A zone purpose as the catalogue names it; words() only for a purpose the catalogue does not know. */
const purposeLabelFrom = (catalogue: Catalogue | null) => (p: string) => catalogue?.zonePurposeLabels?.[p] ?? words(p);

type ZonedAsset = EstateAsset;

/** OT or safety-related: the assets a segmentation assessor looks at first. */
const isOtOrSafety = (a: ZonedAsset) => a.kind === "operational_technology" || !!a.safetyRelated || a.purdueLevel != null;

type EstateView = "unassigned" | "ot" | "findings" | "undeclared";

const ZONE_SORTS: SortOption<Zone>[] = [
  { key: "name", label: "Name", compare: by.text<Zone>((z) => z.name) },
  { key: "assets", label: "Most assets", compare: by.number<Zone>((z) => z._count.assets, "desc") },
  { key: "trust", label: "Set trust level, most trusted first", compare: by.number<Zone>((z) => z.trustLevel, "asc") },
  { key: "purpose", label: "Purpose", compare: by.text<Zone>((z) => z.purpose) },
];

/** The asset CSV: each asset with its zone, Purdue level and safety flag. */
function exportAssets(name: string, rows: ZonedAsset[], zones: Zone[], purdue: Catalogue["purdueLevels"]) {
  const zoneName = new Map(zones.map((z) => [z.id, z.name]));
  downloadCsv(name, [
    { header: "Asset", value: (a: ZonedAsset) => a.name },
    { header: "Kind", value: (a) => words(a.kind) },
    { header: "Criticality", value: (a) => a.criticality },
    { header: "Status", value: (a) => a.status },
    { header: "Zone", value: (a) => (a.zoneId ? zoneName.get(a.zoneId) ?? "" : "") },
    { header: "Purdue level", value: (a) => (a.purdueLevel == null ? "" : purdue?.find((l) => l.value === a.purdueLevel)?.label ?? String(a.purdueLevel / 10)) },
    { header: "Safety related", value: (a) => !!a.safetyRelated },
  ], rows);
}

export function EstatePage() {
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("resilience:write");
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("zones");
  const [view, setView] = useState<EstateView | null>(null);
  const [zoneSearch, setZoneSearch] = useState("");
  const [zoneSort, setZoneSort] = useState("name");
  const [flowReload, setFlowReload] = useState(0);
  const [sites, setSites] = useState<Array<{ id: string; name: string }>>([]);
  const [allAssets, setAllAssets] = useState<ZonedAsset[] | null>(null);
  // The site whose physical security is open; the record itself is read from `physical` so a save shows at once.
  const [openSiteId, setOpenSiteId] = useState<string | null>(null);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [zones, setZones] = useState<Zone[] | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [segmentation, setSegmentation] = useState<Segmentation | null>(null);
  const [physical, setPhysical] = useState<{ sites: PhysicalRecord[]; findings: PhysicalFinding[] } | null>(null);
  // A zone opens read-only ("view"); its form is behind Edit. zoneId null is a new zone.
  const [zoneModal, setZoneModal] = useState<{ zoneId: string | null; mode: "view" | "edit" } | null>(null);
  const [zoneForm, setZoneForm] = useState({ name: "", purpose: "corporate", trustLevel: "", description: "", siteId: "" });
  const [csv, setCsv] = useState("");
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    try {
      const [cat, z, seg, phys] = await Promise.all([
        api.get<Catalogue>("/estate/catalogue"),
        api.get<{ zones: Zone[] }>("/estate/zones"),
        api.get<{ findings: Finding[]; summary: Segmentation }>("/estate/segmentation"),
        api.get<{ sites: PhysicalRecord[]; findings: PhysicalFinding[] }>("/estate/physical-security"),
      ]);
      setCatalogue(cat);
      setZones(z.zones);
      setFindings(seg.findings);
      setSegmentation(seg.summary);
      setPhysical(phys);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the estate."));
      setZones([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    api.get<{ sites: Array<{ id: string; name: string }> }>("/sites").then((d) => setSites(d.sites.map((s) => ({ id: s.id, name: s.name })))).catch(() => setSites([]));
  }, []);
  // Which assets sit in which zone: the tiles, the unassigned queue and the zone detail all read it.
  const loadAssets = useCallback(async () => {
    try { setAllAssets((await api.get<{ assets: ZonedAsset[] }>("/resilience/assets")).assets); }
    catch { setAllAssets([]); }
  }, []);
  useEffect(() => { void loadAssets(); }, [loadAssets]);

  const openZone = useCallback((z: Zone) => {
    setError("");
    setZoneModal({ zoneId: z.id, mode: "view" });
  }, []);
  const editZone = useCallback((z: Zone) => {
    setZoneForm({ name: z.name, purpose: z.purpose, trustLevel: z.trustLevel?.toString() ?? "", description: z.description ?? "", siteId: z.site?.id ?? "" });
    setError("");
    setZoneModal({ zoneId: z.id, mode: "edit" });
  }, []);
  const closeZone = useCallback(() => {
    setZoneModal(null);
    if (searchParams.has("zone")) {
      const next = new URLSearchParams(searchParams);
      next.delete("zone");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ?tab=zones|import|physical opens that tab.
  const deepTab = searchParams.get("tab");
  useEffect(() => {
    if (deepTab === "zones" || deepTab === "import" || deepTab === "physical") setTab(deepTab);
  }, [deepTab]);

  // ?zone=<id> opens that zone, once per id so closing it does not reopen it.
  const deepZone = searchParams.get("zone");
  const openedZone = useRef<string | null>(null);
  useEffect(() => {
    if (!deepZone || !zones || openedZone.current === deepZone) return;
    const target = zones.find((z) => z.id === deepZone);
    if (!target) return;
    openedZone.current = deepZone;
    setTab("zones");
    openZone(target);
  }, [deepZone, zones, openZone]);

  async function placeAsset(assetId: string, zoneId: string | null) {
    setBusy(true); setError("");
    try { await api.put(`/estate/assets/${assetId}/network`, { zoneId }); await Promise.all([loadAssets(), load()]); setFlowReload((n) => n + 1); }
    catch (e) { setError(apiErrorMessage(e, "Could not move the asset.")); }
    finally { setBusy(false); }
  }

  const purposeLabel = useMemo(() => purposeLabelFrom(catalogue), [catalogue]);
  const shownZones = useMemo(() => {
    const rows = (zones ?? []).filter((z) => matchesSearch(zoneSearch, z.name, purposeLabel(z.purpose), z.site?.name, z.description));
    const cmp = ZONE_SORTS.find((o) => o.key === zoneSort)?.compare;
    return cmp ? [...rows].sort(cmp) : rows;
  }, [zones, zoneSearch, zoneSort, purposeLabel]);

  async function saveZone() {
    if (!zoneForm.name.trim()) { setError("A name is required."); return; }
    setBusy(true); setError("");
    const body = {
      name: zoneForm.name.trim(),
      purpose: zoneForm.purpose,
      trustLevel: zoneForm.trustLevel ? Number(zoneForm.trustLevel) : null,
      description: zoneForm.description.trim() || null,
      siteId: zoneForm.siteId || null,
    };
    try {
      // Back to the view of the zone just saved (a new one included), not the list.
      let savedId = zoneModal?.zoneId ?? null;
      if (savedId) await api.put(`/estate/zones/${savedId}`, body);
      else savedId = (await api.post<{ zone: { id: string } }>("/estate/zones", body)).zone?.id ?? null;
      await load();
      setFlowReload((n) => n + 1);
      if (savedId) setZoneModal({ zoneId: savedId, mode: "view" }); else closeZone();
    } catch (e) { setError(apiErrorMessage(e, "Could not save the zone.")); } finally { setBusy(false); }
  }

  async function deleteZone(zone: Zone) {
    setBusy(true); setError("");
    try {
      const res = await api.del<{ unzoned: number }>(`/estate/zones/${zone.id}`);
      setNotice(res.unzoned > 0 ? `${zone.name} removed; ${res.unzoned} ${res.unzoned === 1 ? "asset" : "assets"} left without a zone.` : `${zone.name} removed.`);
      closeZone();
      await Promise.all([load(), loadAssets()]);
      setFlowReload((n) => n + 1);
    } catch (e) { setError(apiErrorMessage(e, "Could not remove the zone.")); } finally { setBusy(false); }
  }

  async function preview() {
    setBusy(true); setError(""); setPlan(null);
    try {
      const res = await api.post<{ plan: ImportPlan }>("/estate/import/preview", { csv });
      setPlan(res.plan);
    } catch (e) { setError(apiErrorMessage(e, "Could not read that file.")); } finally { setBusy(false); }
  }

  async function applyImport() {
    setBusy(true); setError("");
    try {
      const res = await api.post<{ result: { created: number; updated: number; retired: number } }>("/estate/import", { csv });
      setNotice(`${res.result.created} created, ${res.result.updated} updated, ${res.result.retired} retired.`);
      setPlan(null);
      setCsv("");
      await load();
    } catch (e) { setError(apiErrorMessage(e, "The import did not complete.")); } finally { setBusy(false); }
  }

  if (zones === null || !catalogue) return <LoadingBlock />;

  const activeAssets = (allAssets ?? []).filter((a) => a.status === "active");
  const unzonedAssets = activeAssets.filter((a) => !a.zoneId);
  const otAssets = activeAssets.filter(isOtOrSafety);
  const shownFindings = view === "undeclared" ? findings.filter((f) => f.kind === "flow_undeclared") : findings;

  return (
    <div className="page-pad">
      <h1 style={pageHeading}>Network zones</h1>
      <p style={{ ...pageSubtitle, margin: "0 0 20px", maxWidth: 680 }}>
        Where things sit and who can reach them. A zone is a trust boundary; Purdue levels are zones
        with a standard ordering, which is why operational technology and network segmentation share
        one model.
      </p>

      {error && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.danger}`, color: theme.color.danger, fontSize: text.body.fontSize }}>{error}</div>}
      {notice && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}`, ...text.body, color: theme.color.subtle }}>{notice}</div>}

      <IndexTabs<Tab>
        value={tab}
        onChange={setTab}
        style={{ marginBottom: 18 }}
        items={([["zones", "Network zones", Network], ["import", "Import inventory", Upload], ["physical", "Physical security", Building2]] as const).map(([key, label, Icon]) => ({
          id: key,
          label: <><Icon size={14} /> {label}</>,
        }))}
      />

      {tab === "zones" && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ ...text.body, color: theme.color.subtle }}>
              {segmentation && segmentation.total === 0 && zones.length > 0
                ? "No dependency currently crosses a trust boundary."
                : segmentation
                  ? `${segmentation.total} segmentation finding${segmentation.total === 1 ? "" : "s"} — ${segmentation.high} high${segmentation.safetyExposed ? `, ${segmentation.safetyExposed} reaching a safety zone` : ""}`
                  : ""}
            </div>
            {canEdit && (
              <button onClick={() => { setZoneForm({ name: "", purpose: "corporate", trustLevel: "", description: "", siteId: "" }); setError(""); setZoneModal({ zoneId: null, mode: "edit" }); }} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
                <Plus size={15} /> Add a zone
              </button>
            )}
          </div>

          <SummaryTiles tiles={[
            { key: "zones", label: "Zones", value: zones.length, tone: "neutral", hint: view ? "Show everything" : undefined, onClick: () => setView(null), active: view === null },
            { key: "unassigned", label: "Assets with no zone", value: unzonedAssets.length, tone: unzonedAssets.length ? "warning" : "neutral", onClick: () => setView(view === "unassigned" ? null : "unassigned"), active: view === "unassigned" },
            { key: "ot", label: "OT / safety-related assets", value: otAssets.length, tone: "neutral", hint: otAssets.some((a) => !a.zoneId) ? `${otAssets.filter((a) => !a.zoneId).length} with no zone` : undefined, onClick: () => setView(view === "ot" ? null : "ot"), active: view === "ot" },
            { key: "findings", label: "Segmentation findings", value: segmentation?.total ?? findings.length, tone: segmentation?.high ? "danger" : findings.length ? "warning" : "success", hint: segmentation?.high ? `${segmentation.high} high` : undefined, onClick: () => setView(view === "findings" ? null : "findings"), active: view === "findings" },
            { key: "undeclared", label: "Undeclared crossings", value: segmentation?.undeclared ?? 0, tone: segmentation?.undeclared ? "warning" : "neutral", onClick: () => setView(view === "undeclared" ? null : "undeclared"), active: view === "undeclared" },
          ]} />

          {view === "unassigned" && (
            <EstateUnassignedQueue
              assets={allAssets ?? []}
              zones={zones}
              canEdit={canEdit}
              onPlaced={async (message) => { setNotice(message); await Promise.all([load(), loadAssets()]); setFlowReload((n) => n + 1); }}
            />
          )}

          {view === "ot" && (
            <EstateAssetTable
              title="Operational technology and safety-related assets"
              assets={otAssets}
              zones={zones}
              purdue={catalogue.purdueLevels}
              csvName="ot-safety-assets"
            />
          )}

          {view === "undeclared" && (
            <div style={{ marginBottom: 16 }}>
              <ZoneFlowMatrix canEdit={canEdit} reloadKey={flowReload} onChanged={() => void load()} />
            </div>
          )}

          {view === null && zones.length === 0 && (
            <EmptyState title="No zones recorded" detail="Without zones, segmentation cannot be evidenced at all — which is the first question a critical-infrastructure regulator asks." />
          )}
          {view === null && zones.length > 0 && (
            <>
            <RegisterToolbar<Zone>
              search={zoneSearch}
              onSearch={setZoneSearch}
              placeholder="Search zones"
              sortOptions={ZONE_SORTS}
              sort={zoneSort}
              onSort={setZoneSort}
              count={{ shown: shownZones.length, total: zones.length }}
              exportLabel="Export assets CSV"
              onExport={() => exportAssets("assets-by-zone", (allAssets ?? []).filter((a) => a.status === "active"), zones, catalogue.purdueLevels)}
            />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 20 }}>
              {shownZones.map((z) => (
                <button key={z.id} onClick={() => openZone(z)}
                  style={{ ...card, textAlign: "left", cursor: "pointer", border: `1px solid ${theme.color.border}` }}>
                  <div style={text.itemTitle}>{z.name}</div>
                  <div style={{ ...text.hint, marginTop: 2 }}>{purposeLabel(z.purpose)}</div>
                  <div style={{ ...text.meta, marginTop: 6 }}>
                    {z._count.assets} asset{z._count.assets === 1 ? "" : "s"}
                    {z.site && <> · {z.site.name}</>}
                  </div>
                  {(z._count.assets === 0 || !z.description) && (
                    <div style={{ ...text.meta, color: theme.color.warningText, marginTop: 6 }}>
                      {z._count.assets === 0 ? "No assets placed in it yet" : "No description yet"}
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div style={{ marginBottom: 16 }}>
              <ZoneFlowMatrix canEdit={canEdit} reloadKey={flowReload} onChanged={() => void load()} />
            </div>
            {unzonedAssets.length > 0 && (
              <div style={{ ...card, marginBottom: 16, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ ...text.hint, color: theme.color.warningText, flex: 1 }}>
                  {unzonedAssets.length} active asset{unzonedAssets.length === 1 ? " has" : "s have"} no zone, so segmentation cannot be assessed for {unzonedAssets.length === 1 ? "it" : "them"}.
                </span>
                <button type="button" onClick={() => setView("unassigned")} style={secondaryBtn}>Place them</button>
              </div>
            )}
            </>
          )}

          {(view === null || view === "findings" || view === "undeclared") && shownFindings.length > 0 && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <ShieldAlert size={15} color={theme.color.warning} />
                <span style={{ ...text.sectionTitle, margin: 0 }}>
                  {view === "undeclared" ? "Undeclared crossings" : "Segmentation findings"}
                </span>
              </div>
              {shownFindings.map((f) => (
                <div key={f.id} style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 10, marginTop: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: SEVERITY[f.severity]?.bg, color: SEVERITY[f.severity]?.color }}>
                      {words(f.severity)}
                    </span>
                    <span style={text.itemTitle}>{f.title}</span>
                  </div>
                  <div style={{ ...text.hint, marginTop: 4 }}>{f.detail}</div>
                  {(f.zoneIds ?? []).length > 0 && (
                    <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                      {(f.zoneIds ?? []).map((id) => {
                        const z = zones.find((x) => x.id === id);
                        return z ? (
                          <button key={id} type="button" onClick={() => openZone(z)} style={{ ...secondaryBtn, padding: "2px 8px", fontSize: text.meta.fontSize }}>{z.name}</button>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {(view === "findings" || view === "undeclared") && shownFindings.length === 0 && (
            <EmptyState title="Nothing here" detail={view === "undeclared" ? "Every zone pair the dependency graph uses has a declared flow." : "No dependency currently crosses a trust boundary."} />
          )}
        </>
      )}

      {tab === "import" && (
        <div style={card}>
          <div style={{ ...text.sectionTitle, marginBottom: 4 }}>Import an inventory</div>
          <div style={{ ...text.hint, marginBottom: 12 }}>
            Paste a CSV. Nothing is written until you have seen what it would do. Column names are
            matched loosely, so an export from your own spreadsheet usually works unchanged.{" "}
            <button type="button" onClick={() => { void apiBlob("/estate/import/template").then((b) => saveBlob(b, "ot-asset-import-template.csv")).catch((e) => setNotice(apiErrorMessage(e, "Could not download the template."))); }} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", color: theme.color.accent, textDecoration: "underline" }}>Download a template</button>.
          </div>
          <textarea
            value={csv}
            onChange={(e) => { setCsv(e.target.value); setPlan(null); }}
            placeholder="Name,Reference,Criticality,Purdue level,Network zone,Vendor,Firmware…"
            style={{ ...textarea, ...text.mono, minHeight: 160 }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={preview} disabled={!csv.trim() || busy} style={{ ...secondaryBtn, opacity: !csv.trim() || busy ? 0.6 : 1 }}>
              {busy ? "Checking…" : "Preview"}
            </button>
            {plan && (
              <button onClick={applyImport} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
                Apply: {plan.create.length} new, {plan.update.length} updated, {plan.retire.length} retired
              </button>
            )}
          </div>

          {plan && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${theme.color.border}` }}>
              <div style={{ ...text.hint, marginBottom: 8 }}>
                {plan.create.length} would be created, {plan.update.length} updated, {plan.unchanged} unchanged,
                {" "}{plan.retire.length} retired.
              </div>
              {plan.unknownColumns.length > 0 && (
                <div style={{ ...text.hint, color: theme.color.warning, marginBottom: 6 }}>
                  Columns not recognised and ignored: {plan.unknownColumns.join(", ")}
                </div>
              )}
              {plan.issues.length > 0 && (
                <div style={{ ...text.hint, maxHeight: 220, overflowY: "auto" }}>
                  {plan.issues.map((iss, i) => (
                    <div key={i} style={{ color: theme.color.subtle, marginBottom: 3 }}>
                      {iss.line > 0 && <strong style={{ color: theme.color.text }}>Row {iss.line}: </strong>}{iss.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "physical" && physical && (
        <>
          {physical.sites.filter((s) => s.inScope).length === 0 ? (
            <EmptyState title="No sites in scope" detail="Add a site under Sites & locations first; physical security is recorded per site." />
          ) : (
            <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16 }}>
              {physical.sites.filter((s) => s.inScope).map((s, i) => (
                <button
                  key={s.siteId}
                  type="button"
                  onClick={() => setOpenSiteId(s.siteId)}
                  style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", fontFamily: theme.font, padding: "12px 16px", borderTop: i === 0 ? "none" : `1px solid ${theme.color.border}` }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div>
                      <span style={text.itemTitle}>{s.siteName}</span>
                      <span style={{ ...text.meta, marginLeft: 8 }}>{words(s.siteKind)}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {physical.findings.filter((f) => f.siteId === s.siteId).length > 0 && (
                        <span style={{ ...text.meta, color: theme.color.warningText }}>{physical.findings.filter((f) => f.siteId === s.siteId).length} finding{physical.findings.filter((f) => f.siteId === s.siteId).length === 1 ? "" : "s"}</span>
                      )}
                      <span style={{ ...text.meta, color: s.reviewedAt ? theme.color.subtle : theme.color.warningText }}>
                        {s.reviewedAt ? `Recorded ${fmtDay(s.reviewedAt)}` : "Not yet recorded"}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {physical.findings.length > 0 && (
            <div style={card}>
              <div style={{ ...text.sectionTitle, marginBottom: 4 }}>Findings</div>
              <div style={{ ...text.meta, marginBottom: 10 }}>
                Each cites the ISO 27001 A.7 control it evidences. An unanswered question is a prompt, not a failure.
              </div>
              {physical.findings.map((f) => (
                <div key={f.id} style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 10, marginTop: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: SEVERITY[f.severity]?.bg, color: SEVERITY[f.severity]?.color }}>
                      {words(f.severity)}
                    </span>
                    <span style={text.itemTitle}>{f.title}</span>
                    <span style={text.meta}>{f.control}</span>
                  </div>
                  <div style={{ ...text.hint, marginTop: 4 }}>{f.detail}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {zoneModal && (() => {
        const zone = zoneModal.zoneId ? zones.find((z) => z.id === zoneModal.zoneId) ?? null : null;
        // A zone removed elsewhere while open: nothing left to show.
        if (zoneModal.zoneId && !zone) return null;
        if (zone && zoneModal.mode === "view") {
          return (
            <ModalOverlay onClose={closeZone} wide>
              <ModalHeader title={zone.name} onClose={closeZone} right={canEdit ? <EditButton onClick={() => editZone(zone)} /> : undefined} />
              <div style={{ padding: 20 }}>
                <ZoneView
                  zone={zone}
                  assets={allAssets}
                  findings={findings}
                  zones={zones}
                  purdue={catalogue.purdueLevels}
                  purposeLabel={purposeLabel}
                  canEdit={canEdit}
                  busy={busy}
                  onPlace={placeAsset}
                  flowReload={flowReload}
                  onFlowsChanged={() => void load()}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                  <button onClick={closeZone} style={secondaryBtn}>Close</button>
                </div>
              </div>
            </ModalOverlay>
          );
        }
        return (
          <ModalOverlay onClose={closeZone}>
            <ModalHeader title={zone ? `Edit ${zone.name}` : "Add a zone"} onClose={closeZone} />
            <div style={{ padding: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
                <Field label="Name" span2><input value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} style={input} /></Field>
                <Field label="Purpose">
                  <select value={zoneForm.purpose} onChange={(e) => setZoneForm({ ...zoneForm, purpose: e.target.value })} style={input}>
                    {catalogue.zonePurposes.map((p) => <option key={p} value={p}>{purposeLabel(p)}</option>)}
                  </select>
                </Field>
                <Field label="Trust level" help="0 is most trusted. Leave blank to use the purpose's default.">
                  <input type="number" min={0} max={10} value={zoneForm.trustLevel} onChange={(e) => setZoneForm({ ...zoneForm, trustLevel: e.target.value })} style={input} />
                </Field>
                <Field label="Description" span2>
                  <textarea value={zoneForm.description} onChange={(e) => setZoneForm({ ...zoneForm, description: e.target.value })} style={textarea} />
                </Field>
                <Field label="Site" span2 help="Where the zone physically sits, if it is one place.">
                  <select value={zoneForm.siteId} onChange={(e) => setZoneForm({ ...zoneForm, siteId: e.target.value })} style={input}>
                    <option value="">Not tied to one site</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginTop: 16 }}>
                <div>{zone && <button onClick={() => deleteZone(zone)} disabled={busy} style={dangerBtn}>Delete</button>}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => (zone ? setZoneModal({ zoneId: zone.id, mode: "view" }) : closeZone())} style={secondaryBtn}>Cancel</button>
                  <button onClick={saveZone} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>Save</button>
                </div>
              </div>
            </div>
          </ModalOverlay>
        );
      })()}

      {openSiteId && physical && (() => {
        const site = physical.sites.find((s) => s.siteId === openSiteId);
        if (!site) return null;
        return (
          <PhysicalRecordModal
            key={site.siteId}
            site={site}
            catalogue={catalogue.physical}
            findings={physical.findings.filter((f) => f.siteId === site.siteId)}
            canEdit={canEdit}
            onClose={() => setOpenSiteId(null)}
            onSaved={load}
          />
        );
      })()}
    </div>
  );
}

// ── Zone view ─────────────────────────────────────────────────────────────────
// The read-only view of one zone: its details, what it still lacks, the assets in it, the
// segmentation findings that name it and its row of the flow matrix. Placing an asset here or
// moving one out changes the zone, so those sit under Actions for people who may edit.

function ZoneView({ zone, assets, findings, zones, purdue, purposeLabel, canEdit, busy, onPlace, flowReload, onFlowsChanged }: {
  zone: Zone;
  assets: ZonedAsset[] | null;
  findings: Finding[];
  zones: Zone[];
  purdue: Catalogue["purdueLevels"];
  purposeLabel: (purpose: string) => string;
  canEdit: boolean;
  busy: boolean;
  onPlace: (assetId: string, zoneId: string | null) => Promise<void>;
  flowReload: number;
  onFlowsChanged: () => void;
}) {
  const [adding, setAdding] = useState("");
  const [removing, setRemoving] = useState("");
  const inZone = (assets ?? []).filter((a) => a.zoneId === zone.id);
  const unzoned = (assets ?? []).filter((a) => a.status === "active" && !a.zoneId);
  // Every finding carries the zones it concerns (lib/networkZones.ts), so no name matching.
  const touching = findings.filter((f) => (f.zoneIds ?? []).includes(zone.id));
  const steps: NextStepItem[] = [
    { done: !!zone.description?.trim(), label: "Describe what the zone holds and who can reach it" },
    { done: zone.trustLevel !== null, label: "Set the trust level", hint: "Otherwise the purpose's default is used." },
    { done: inZone.length > 0 || zone._count.assets > 0, label: "Place the assets that sit in this zone", hint: "Segmentation findings are worked out from the assets and their dependencies." },
    ...touching.filter((f) => f.severity === "high").map((f) => ({ done: false, label: f.title, hint: f.detail, urgent: true })),
  ];
  const links: RelatedLink[] = [
    ...(zone.site ? [{ kindLabel: "Site", label: zone.site.name, href: `/sites/${zone.site.id}` }] : []),
    ...inZone.map((a) => ({ kindLabel: "Asset", label: `${a.name} (${a.criticality})`, href: KIND_HREFS.asset(a.id) })),
  ];
  const assetCount = assets === null ? zone._count.assets : inZone.length;
  const showActions = canEdit && assets !== null && (unzoned.length > 0 || inZone.length > 0);
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <NextSteps items={steps} />
      <DetailSection title="Details">
        <DetailFields fields={[
          { label: "Purpose", value: purposeLabel(zone.purpose) },
          { label: "Trust level", value: zone.trustLevel !== null ? `${zone.trustLevel} (0 is most trusted)` : "The purpose's default" },
          { label: "Site", value: zone.site?.name },
          { label: "Assets", value: String(assetCount) },
          { label: "Description", value: zone.description, wide: true, multiline: true },
        ]} />
      </DetailSection>
      <DetailSection title={`Assets in this zone (${assetCount})`}>
        {assets === null ? <div style={{ ...text.hint, color: theme.color.faint }}>Loading…</div> : inZone.length === 0 ? (
          <div style={{ ...text.hint, color: theme.color.faint }}>No assets placed in this zone yet.</div>
        ) : (
          <EstateAssetTable
            bare
            assets={inZone}
            zones={zones}
            purdue={purdue}
            csvName={`zone-${zone.name.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}-assets`}
          />
        )}
      </DetailSection>
      {touching.length > 0 && (
        <DetailSection title="Segmentation findings for this zone">
          {touching.map((f) => (
            <div key={f.id} style={{ ...text.hint, color: theme.color.text, marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: SEVERITY[f.severity]?.bg, color: SEVERITY[f.severity]?.color, marginRight: 6 }}>{words(f.severity)}</span>
              <span style={{ color: theme.color.text, fontWeight: 600 }}>{f.title}</span>
              <div style={{ color: theme.color.subtle, marginTop: 2 }}>{f.detail}</div>
            </div>
          ))}
        </DetailSection>
      )}
      {zones.length > 1 && <ZoneFlowMatrix canEdit={canEdit} reloadKey={flowReload} onChanged={onFlowsChanged} focusZoneId={zone.id} />}
      <RelatedRecords entity={null} links={links} />
      {showActions && (
        <RecordActions>
          {unzoned.length > 0 && (
            <div style={{ display: "flex", gap: 8, flex: "1 1 320px", flexWrap: "wrap" }}>
              <select value={adding} onChange={(e) => setAdding(e.target.value)} style={{ ...input, flex: 1, minWidth: 180 }} aria-label="Asset to place in this zone">
                <option value="">Place an unzoned asset here…</option>
                {unzoned.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.criticality})</option>)}
              </select>
              <button type="button" disabled={!adding || busy} onClick={async () => { await onPlace(adding, zone.id); setAdding(""); }} style={{ ...secondaryBtn, opacity: !adding || busy ? 0.6 : 1 }}>Place</button>
            </div>
          )}
          {inZone.length > 0 && (
            <div style={{ display: "flex", gap: 8, flex: "1 1 320px", flexWrap: "wrap" }}>
              <select value={removing} onChange={(e) => setRemoving(e.target.value)} style={{ ...input, flex: 1, minWidth: 180 }} aria-label="Asset to move out of this zone">
                <option value="">Move an asset out of this zone…</option>
                {inZone.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.criticality})</option>)}
              </select>
              <button type="button" disabled={!removing || busy} onClick={async () => { await onPlace(removing, null); setRemoving(""); }} style={{ ...secondaryBtn, opacity: !removing || busy ? 0.6 : 1 }}>Move out</button>
            </div>
          )}
        </RecordActions>
      )}
    </div>
  );
}

// ── Physical security per site ────────────────────────────────────────────────
// Opens read-only: the answers, what is still missing and the findings. The questionnaire
// PUT /estate/physical-security/:siteId takes sits behind Edit; saving it is what "recorded"
// means, so every save stamps the review date server-side.

type PhysicalValues = {
  accessControl: string; visitorManagement: string; cctvCoverage: string; cctvRetentionDays: string;
  /** "" = not answered, "yes", "no": an unticked box is not an answer (RES-28). */
  intruderAlarm: TriState; alarmMonitored: TriState; environmentalMonitoring: string[]; fireSuppression: string;
  powerResilience: string[]; powerTestedAt: string; secureAreas: TriState; clearDeskPolicy: TriState; equipmentDisposal: string;
};
type TriState = "" | "yes" | "no";
const toTri = (v: boolean | null): TriState => (v === null ? "" : v ? "yes" : "no");
const fromTri = (v: TriState): boolean | null => (v === "" ? null : v === "yes");

const physicalValues = (site: PhysicalRecord): PhysicalValues => ({
  accessControl: site.accessControl ?? "", visitorManagement: site.visitorManagement ?? "", cctvCoverage: site.cctvCoverage ?? "",
  cctvRetentionDays: site.cctvRetentionDays?.toString() ?? "", intruderAlarm: toTri(site.intruderAlarm), alarmMonitored: toTri(site.alarmMonitored),
  environmentalMonitoring: site.environmentalMonitoring, fireSuppression: site.fireSuppression ?? "", powerResilience: site.powerResilience,
  powerTestedAt: toDateInput(site.powerTestedAt), secureAreas: toTri(site.secureAreas), clearDeskPolicy: toTri(site.clearDeskPolicy), equipmentDisposal: site.equipmentDisposal ?? "",
});

function physicalSteps(f: PhysicalValues, findings: PhysicalFinding[]): NextStepItem[] {
  return [
    { done: !!f.accessControl, label: "Say how entry is controlled (ISO 27001 A.7.2)" },
    { done: !!f.visitorManagement, label: "Say how visitors are managed" },
    { done: !!f.cctvCoverage, label: "Record the CCTV coverage (A.7.4)" },
    { done: !!f.fireSuppression, label: "Record the fire protection (A.7.5)" },
    { done: !!f.equipmentDisposal, label: "Say how equipment is disposed of (A.7.14)" },
    { done: f.powerResilience.length === 0 || !!f.powerTestedAt, label: "Record when the backup power was last tested (A.7.11)" },
    ...findings.map((x) => ({ done: false, label: x.title, hint: `${x.detail} (${x.control})`, urgent: x.severity === "high" })),
  ];
}

function PhysicalRecordModal({ site, catalogue, findings, canEdit, onClose, onSaved }: {
  site: PhysicalRecord;
  catalogue: Record<string, string[]>;
  findings: PhysicalFinding[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [f, setF] = useState<PhysicalValues>(() => physicalValues(site));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = <K extends keyof PhysicalValues>(k: K, v: PhysicalValues[K]) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k: "environmentalMonitoring" | "powerResilience", v: string) => set(k, f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v]);
  const opts = (key: string) => catalogue[key] ?? [];
  const yesNo = (v: TriState) => (v === "yes" ? "Yes" : v === "no" ? "No" : null);
  const list = (v: string[]) => (v.length ? v.map(words).join(", ") : null);

  function startEdit() { setF(physicalValues(site)); setError(""); setMode("edit"); }

  async function submit() {
    setSaving(true); setError("");
    try {
      await api.put(`/estate/physical-security/${site.siteId}`, {
        accessControl: f.accessControl || null, visitorManagement: f.visitorManagement || null, cctvCoverage: f.cctvCoverage || null,
        cctvRetentionDays: f.cctvRetentionDays.trim() === "" ? null : Number(f.cctvRetentionDays),
        intruderAlarm: fromTri(f.intruderAlarm), alarmMonitored: fromTri(f.alarmMonitored), environmentalMonitoring: f.environmentalMonitoring,
        fireSuppression: f.fireSuppression || null, powerResilience: f.powerResilience, powerTestedAt: fromInput(f.powerTestedAt),
        secureAreas: fromTri(f.secureAreas), clearDeskPolicy: fromTri(f.clearDeskPolicy), equipmentDisposal: f.equipmentDisposal || null,
      });
      await onSaved();
      // Back to the view, which now reads the saved record.
      setMode("view");
    } catch (e) { setError(apiErrorMessage(e, "Could not save the site's physical security.")); } finally { setSaving(false); }
  }

  const select = (key: "accessControl" | "visitorManagement" | "cctvCoverage" | "fireSuppression" | "equipmentDisposal", catalogueKey: string) => (
    <select value={f[key]} onChange={(e) => set(key, e.target.value)} style={input}>
      <option value="">Not answered</option>
      {opts(catalogueKey).map((o) => <option key={o} value={o}>{words(o)}</option>)}
    </select>
  );
  const chips = (key: "environmentalMonitoring" | "powerResilience") => (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {opts(key).map((o) => {
        const on = f[key].includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(key, o)} style={{
            padding: "3px 10px", borderRadius: 3, fontSize: text.hint.fontSize, cursor: "pointer",
            border: on ? `2px solid ${theme.color.accent}` : `1px solid ${theme.color.border}`, background: on ? theme.color.accentBg : theme.color.panel,
            color: on ? theme.color.accentText : theme.color.muted, fontWeight: on ? 600 : 400,
          }}>{words(o)}</button>
        );
      })}
    </div>
  );

  if (mode === "view") {
    const v = physicalValues(site);
    return (
      <ModalOverlay onClose={onClose} wide>
        <ModalHeader
          title={`Physical security — ${site.siteName}`}
          onClose={onClose}
          right={canEdit ? <EditButton onClick={startEdit} label={site.reviewedAt ? "Edit" : "Record"} /> : undefined}
        />
        <div style={{ padding: 20 }}>
          <NextSteps items={physicalSteps(v, findings)} />
          <DetailSection title="Access and surveillance">
            <DetailFields fields={[
              { label: "Entry control", value: v.accessControl ? words(v.accessControl) : null },
              { label: "Visitors", value: v.visitorManagement ? words(v.visitorManagement) : null },
              { label: "CCTV coverage", value: v.cctvCoverage ? words(v.cctvCoverage) : null },
              { label: "CCTV retention (days)", value: v.cctvRetentionDays },
              { label: "Intruder alarm fitted", value: yesNo(v.intruderAlarm) },
              { label: "Alarm monitored by a receiving centre", value: yesNo(v.alarmMonitored) },
              { label: "Secure areas have their own control", value: yesNo(v.secureAreas) },
              { label: "Clear desk and clear screen in force", value: yesNo(v.clearDeskPolicy) },
            ]} />
          </DetailSection>
          <DetailSection title="Environment, power and disposal">
            <DetailFields fields={[
              { label: "Fire protection", value: v.fireSuppression ? words(v.fireSuppression) : null },
              { label: "Equipment disposal", value: v.equipmentDisposal ? words(v.equipmentDisposal) : null },
              { label: "Environmental monitoring", value: list(v.environmentalMonitoring) },
              { label: "Power resilience", value: list(v.powerResilience) },
              { label: "Backup power last tested", value: site.powerTestedAt ? fmtDateOnly(site.powerTestedAt) : null },
              { label: "Last recorded", value: site.reviewedAt ? fmtDay(site.reviewedAt) : "Not yet recorded" },
            ]} />
          </DetailSection>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button onClick={onClose} style={secondaryBtn}>Close</button>
          </div>
        </div>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={`Physical security — ${site.siteName} (editing)`} onClose={onClose} />
      <div style={{ padding: 20 }}>
        <NextSteps items={physicalSteps(f, findings)} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px 16px" }}>
          <Field label="Entry control">{select("accessControl", "accessControl")}</Field>
          <Field label="Visitors">{select("visitorManagement", "visitorManagement")}</Field>
          <Field label="CCTV coverage">{select("cctvCoverage", "cctvCoverage")}</Field>
          <Field label="CCTV retention (days)"><input type="number" min={0} value={f.cctvRetentionDays} onChange={(e) => set("cctvRetentionDays", e.target.value)} style={input} /></Field>
          <Field label="Fire protection">{select("fireSuppression", "fireSuppression")}</Field>
          <Field label="Equipment disposal">{select("equipmentDisposal", "equipmentDisposal")}</Field>
          <Field label="Environmental monitoring" span2>{chips("environmentalMonitoring")}</Field>
          <Field label="Power resilience" span2>{chips("powerResilience")}</Field>
          <Field label="Backup power last tested"><input type="date" value={f.powerTestedAt} max={todayInput()} onChange={(e) => set("powerTestedAt", e.target.value)} style={input} /></Field>
          {([
            ["intruderAlarm", "Intruder alarm fitted"],
            ["alarmMonitored", "Alarm monitored by a receiving centre"],
            ["secureAreas", "Secure areas (server room, records) have their own control"],
            ["clearDeskPolicy", "Clear desk and clear screen in force"],
          ] as const).map(([key, label]) => (
            <Field key={key} label={label}>
              <select value={f[key]} onChange={(e) => set(key, e.target.value as TriState)} style={input}>
                <option value="">Not answered</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </Field>
          ))}
        </div>
        {error && <div style={{ ...text.hint, color: theme.color.dangerText, marginTop: 10 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={() => { setMode("view"); setError(""); }} disabled={saving} style={secondaryBtn}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </ModalOverlay>
  );
}
